/**
 * ChantierDuJour — Widget de pointage unifié (haut de Mon Espace, terrain uniquement)
 *
 * Gère tous les cas de figure en UN SEUL widget :
 *  1. Aucun BT assigné → Fallback "Pointage rapide" (lien vers /pointage)
 *  2. BT standard assigné → Vue riche (client, adresse, équipe, CTA punch)
 *  3. BT interne assigné → Vue enrichie (catégorie, description, heures budgétées)
 *  4. En service (punché) → Timer temps réel + infos chantier
 *  5. En pause → Timer de pause + couleur ambrée
 *
 * Remplace l'ancien duo ChantierDuJour + QuickPunchWidget pour supprimer la duplication.
 */
import axios from 'axios';
import { dateDuJourQuebec } from '@/lib/dateQuebec';  // 🧱 Phase 4 — 4.1 (B-1)
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Clock, Users, ExternalLink, CheckCircle2, Circle,
  ArrowRight, Sun, Briefcase, Navigation, Coffee,
  LogIn, ChevronRight, Building2, FileText, Target,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDureeLive } from '@/lib/dureeFormat';  // 🎨 [v13.54] format non-ambigu
// 🆕 [Phase 81 Popover — 2026-06-22] Popover explicatif "Heures en attente d'approbation"
import HeuresEnAttentePopover from './HeuresEnAttentePopover';
import { estProprietaire, aLeRole } from '@/lib/roles'; // 👥 E3b — rôles multiples

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

function useElapsed(punchInTime, pauseStart, heuresPause) {
  const [elapsed, setElapsed] = useState('');
  useEffect(() => {
    if (!punchInTime) { setElapsed(''); return; }
    const update = () => {
      const pausedMs = parseFloat(heuresPause || 0) * 3600 * 1000;
      const activeEnd = pauseStart ? new Date(pauseStart).getTime() : Date.now();
      const diff = Math.floor((activeEnd - new Date(punchInTime).getTime() - pausedMs) / 1000);
      // 🎨 [Phase v13.54 — 2026-07-21] Format non-ambigu "8h 55min" (base 60).
      // Voir /app/memory/rca_v13_54_3_taches.md §Tâche 2.
      setElapsed(formatDureeLive(Math.max(0, diff)));
    };
    update();
    if (pauseStart) return; // figé pendant la pause
    const t = setInterval(update, 30000);
    return () => clearInterval(t);
  }, [punchInTime, pauseStart, heuresPause]);
  return elapsed;
}

function usePauseElapsed(pauseStart) {
  const [pauseElapsed, setPauseElapsed] = useState('');
  useEffect(() => {
    if (!pauseStart) { setPauseElapsed(''); return; }
    const update = () => {
      const diff = Math.floor((Date.now() - new Date(pauseStart).getTime()) / 1000);
      const m = Math.floor(diff / 60);
      setPauseElapsed(`${m} min`);
    };
    update();
    const t = setInterval(update, 30000);
    return () => clearInterval(t);
  }, [pauseStart]);
  return pauseElapsed;
}

export function ChantierDuJour() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [bons, setBons] = useState([]);
  const [currentPunch, setCurrentPunch] = useState(null);
  const [todayPunches, setTodayPunches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [punching, setPunching] = useState(false);

  const pauseStart = currentPunch?.pause_start_time || null;
  const heuresPause = currentPunch?.heures_pause || 0;
  const elapsed = useElapsed(currentPunch?.punch_in_time, pauseStart, heuresPause);
  const pauseElapsed = usePauseElapsed(pauseStart);

  // Cat H — widget "Chantier du jour" est du terrain (peintres/chefs/installateurs) : masqué pour admins et Owner par nature (ils ne pointent pas sur chantiers).
  const isAdmin = user?.type_employe === 'admin' || estProprietaire(user);

  const fetchData = useCallback(async () => {
    if (!token || isAdmin) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API_URL}/api/punch/mes-bons-aujourd-hui`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 200) {
        const d = res.data;
        setBons(d.bons || []);
        setCurrentPunch(d.current_punch || null);
        setTodayPunches(d.today_punches || []);
      }
    } catch { /* offline */ }
    finally { setLoading(false); }
  }, [token, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (isAdmin || loading) return null;

  // 🆕 [6.4-frontend] Split today / tomorrow basé sur les flags backend
  // (`is_tomorrow` + `day_label`). On garde la logique riche existante uniquement
  // sur les BT d'aujourd'hui, et on rend une section dédiée pour demain en bas.
  const bonsToday = bons.filter(b => !b.is_tomorrow);
  const bonsTomorrow = bons.filter(b => b.is_tomorrow);
  // Tri par ordre_journalier dans chaque liste (les groupes sont distincts)
  bonsToday.sort((a, b) => (a.ordre_journalier ?? 99) - (b.ordre_journalier ?? 99));
  bonsTomorrow.sort((a, b) => (a.ordre_journalier ?? 99) - (b.ordre_journalier ?? 99));

  const bon = bonsToday[0];
  const otherJobs = bonsToday.slice(1);
  const isPunched = !!currentPunch;
  const isPaused = !!pauseStart;
  const isInternal = !!bon?.est_interne;
  const isServiceCall = !!bon?._is_service_call;

  // Cumul heures aujourd'hui
  const totalHeuresAujourdhui = todayPunches
    .filter(p => p.heures_nettes)
    .reduce((s, p) => s + (p.heures_nettes || 0), 0);

  // ─── CAS 1 : aucun BT aujourd'hui ET pas punché ─────────────────────────────
  if (!bon && !isPunched) {
    // 🆕 [6.4-frontend] Si on a des BT pour demain, on les met en avant.
    if (bonsTomorrow.length > 0) {
      const tomorrowDate = (bonsTomorrow[0]?.dates_chantier || []).find(d => d > dateDuJourQuebec()) || '';
      return (
        <div
          data-testid="chantier-du-jour-tomorrow-only"
          className="rounded-2xl border-2 border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-4 shadow-sm space-y-3"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center shrink-0">
              <Sun className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Aujourd&apos;hui</p>
              <p className="text-sm font-bold text-amber-700 dark:text-amber-200">Pas de chantier aujourd&apos;hui</p>
            </div>
          </div>
          <div className="border-t border-amber-200 pt-3" data-testid="section-tomorrow">
            <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-2">
              🔜 Demain {tomorrowDate ? `(${tomorrowDate})` : ''} — {bonsTomorrow.length} bon{bonsTomorrow.length > 1 ? 's' : ''}
            </p>
            <ul className="space-y-1.5">
              {bonsTomorrow.map((j) => (
                <li
                  key={j.id}
                  className="flex items-start gap-2 rounded-lg p-2 text-xs bg-white border border-amber-200"
                  data-testid={`tomorrow-job-${j.id}`}
                >
                  <Badge className="flex-shrink-0 text-[10px] h-5 bg-amber-100 text-amber-800">Demain</Badge>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">
                      {j._is_service_call ? '🔧 ' : ''}{j.client_nom || j.nom_dossier || j.description_travaux || 'Tâche'}
                    </p>
                    {j.adresse && <p className="text-[11px] text-slate-500 truncate">{j.adresse}</p>}
                  </div>
                </li>
              ))}
            </ul>
            <Button
              disabled
              className="w-full mt-2 bg-slate-200 text-slate-500 cursor-not-allowed h-9 text-xs"
              title={`Disponible à partir du ${tomorrowDate || 'jour du chantier'}`}
              onClick={() => toast.info("Tu pourras te punch in demain matin")}
              data-testid="punch-in-tomorrow-disabled"
            >
              <LogIn className="w-3.5 h-3.5 mr-1.5" />
              Punch in disponible demain
            </Button>
          </div>
        </div>
      );
    }
    // Fallback original : aucun BT aujourd'hui ni demain
    return (
      <div
        data-testid="chantier-du-jour-fallback"
        className="rounded-2xl border-2 border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700 p-4 shadow-sm"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shrink-0">
            <Clock className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Pointage</p>
            <p className="text-sm font-bold text-blue-700 dark:text-blue-300">Aucun chantier assigné aujourd&apos;hui</p>
          </div>
        </div>
        <Button
          onClick={() => navigate('/pointage')}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold h-11"
          data-testid="goto-pointage-btn"
        >
          <LogIn className="w-4 h-4 mr-2" />
          Aller à la pointeuse
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    );
  }

  // ─── CAS 2 à 5 : BT présent OU punché → vue riche ──────────────────────────
  const handlePunchIn = async () => {
    if (!bon || !user?.employee_id) { navigate('/pointage'); return; }
    setPunching(true);
    try {
      const { requestGPS } = await import('../lib/gpsUtils');
      const gps = await requestGPS({ required: true });
      if (!gps) { setPunching(false); return; }

      const body = {
        employee_id: user.employee_id,
        employee_nom: `${user.prenom || ''} ${user.nom || ''}`.trim(),
        bon_travail_id: bon.id,
        contrat_id: bon.contrat_id || bon.dossier_id,
        type_punch: isInternal ? 'interne' : 'chantier',
        latitude: gps.latitude,
        longitude: gps.longitude,
      };
      const res = await axios.post(`${API_URL}/api/punch/in`, body);
      if (res.status === 200) {
        toast.success('Punch IN enregistré !');
        await fetchData();
      } else {
        toast.error('Erreur punch — essayez depuis la page Pointage');
        navigate('/pointage');
      }
    } finally { setPunching(false); }
  };

  const handlePunchOut = async () => {
    if (!currentPunch?.id) { navigate('/pointage'); return; }
    setPunching(true);
    try {
      const { requestGPS } = await import('../lib/gpsUtils');
      const gps = await requestGPS({ required: true });
      if (!gps) { setPunching(false); return; }

      const res = await axios.post(`${API_URL}/api/punch/out`, {
        punch_id: currentPunch.id,
        latitude: gps.latitude,
        longitude: gps.longitude,
      });
      if (res.status === 200) {
        toast.success('Bonne journée ! Punch OUT enregistré.');
        setCurrentPunch(null);
        await fetchData();
      } else {
        navigate('/pointage');
      }
    } finally { setPunching(false); }
  };

  // Couleurs selon état
  const cardClass = isPaused
    ? 'border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 dark:border-amber-600'
    : isPunched
      ? 'border-green-400 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 dark:border-green-600'
      : isServiceCall
        ? 'border-orange-400 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 dark:border-orange-500'
        : isInternal
          ? 'border-purple-300 bg-gradient-to-br from-purple-50 to-slate-50 dark:from-purple-900/20 dark:to-slate-900/20 dark:border-purple-500'
          : 'border-orange-300 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 dark:border-orange-500';

  const statusText = isPaused
    ? `En pause — ${pauseElapsed}`
    : isPunched
      ? `En service — ${elapsed}`
      : isServiceCall
        ? `🔧 Service Call — 1er chantier`
        : isInternal
          ? 'Opération interne du jour'
          : (bons.length > 1 ? `Chantier du jour — ${bon?.ordre_journee_index || 1}${(bon?.ordre_journee_index || 1) === 1 ? 'er' : 'e'} / ${bon?.total_jobs_today || bons.length}` : 'Chantier du jour');

  const statusColor = isPaused
    ? 'text-amber-700 dark:text-amber-400'
    : isPunched
      ? 'text-green-700 dark:text-green-400'
      : isServiceCall
        ? 'text-orange-700 dark:text-orange-300 font-bold'
        : isInternal
          ? 'text-purple-700 dark:text-purple-300'
          : 'text-orange-700 dark:text-orange-400';

  const statusIcon = isPaused
    ? <Coffee className="w-5 h-5 text-amber-600" />
    : isPunched
      ? <CheckCircle2 className="w-5 h-5 text-green-600" />
      : <Circle className="w-5 h-5 text-orange-500" />;

  const mapsUrl = bon?.adresse
    ? `https://maps.google.com/?q=${encodeURIComponent(bon.adresse)}`
    : null;

  // Titre principal selon type de BT
  const titrePrincipal = isServiceCall
    ? (bon?.client_nom || bon?.nom_dossier || 'Service Call')
    : isInternal
      ? (bon?.description_travaux || bon?.categorie_interne_nom || 'Tâche interne')
      : (bon?.client_nom || bon?.nom_dossier || 'Chantier');

  return (
    <Card
      className={`border-2 transition-colors ${cardClass}`}
      data-testid="chantier-du-jour-card"
    >
      <CardContent className="p-4 space-y-3">

        {/* Header status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {statusIcon}
            <span className={`font-semibold text-sm ${statusColor}`}>
              {statusText}
            </span>
          </div>
          {isPunched && totalHeuresAujourdhui > 0 && (
            <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 text-xs">
              {totalHeuresAujourdhui.toFixed(2).replace('.', ',')}h aujourd&apos;hui
            </Badge>
          )}
        </div>

        {/* Infos chantier / opération interne */}
        {bon && (
          <div className="space-y-2">
            {/* Titre + type badge */}
            <div className="flex items-start gap-2">
              {isInternal
                ? <Building2 className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                : <Briefcase className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
              }
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-800 dark:text-slate-100 leading-tight break-words">
                  {titrePrincipal}
                </p>
                {/* Sous-titre contextuel */}
                {isInternal && bon.categorie_interne_nom && bon.description_travaux && (
                  <p className="text-xs text-purple-700 dark:text-purple-300 truncate font-medium">
                    📂 {bon.categorie_interne_nom}
                  </p>
                )}
                {!isInternal && bon.nom_dossier && bon.client_nom && (
                  <p className="text-xs text-slate-500 truncate">{bon.nom_dossier}</p>
                )}
              </div>
              <Badge
                className={`flex-shrink-0 text-xs ${
                  isServiceCall
                    ? 'bg-orange-200 text-orange-900 font-bold'
                    : isInternal
                      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                      : bon.type_travaux === 'exterieur'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-blue-100 text-blue-700'
                }`}
                data-testid="bon-type-badge"
              >
                {isServiceCall ? '🔧 Service Call' : isInternal ? '🏢 Interne' : (bon.type_travaux === 'exterieur' ? '☀️ Ext.' : '☁️ Int.')}
              </Badge>
            </div>

            {/* Description / notes (BT interne → notes importantes) */}
            {isInternal && bon.notes_chantier && (
              <div className="flex items-start gap-2 text-slate-700 dark:text-slate-300 text-sm bg-white/60 dark:bg-slate-800/40 rounded-lg px-2.5 py-2" data-testid="bt-internal-notes">
                <FileText className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs leading-relaxed break-words whitespace-pre-wrap line-clamp-3">
                  {bon.notes_chantier}
                </p>
              </div>
            )}

            {/* Heures budgétées (BT interne) — 🆕 [Phase 81 — 2026-06-22]
                Défense en profondeur : si le backend a masqué (heures_masquees_pour_role)
                ou si pas approuvé (heures_budgetees_officielles == null), afficher
                la mention neutre "Heures en attente d'approbation" au lieu du nombre. */}
            {isInternal && (() => {
              const masquees = bon.heures_masquees_pour_role === true;
              const officielles = bon.heures_budgetees_officielles;
              const officiellesAbsentes = (officielles === null || officielles === undefined);
              if (masquees || officiellesAbsentes) {
                // Si admin-like le backend renvoie tout : seul un worker tombe ici quand non approuvé
                return (
                  <div className="flex items-center gap-2" data-testid="bt-internal-heures-en-attente">
                    <Target className="w-4 h-4 text-slate-400 shrink-0" />
                    <HeuresEnAttentePopover />
                  </div>
                );
              }
              if (bon.heures_budgetees > 0) {
                return (
                  <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400 text-sm" data-testid="bt-internal-heures">
                    <Target className="w-4 h-4 text-purple-500" />
                    <span>Budget : <strong>{Number(bon.heures_budgetees).toFixed(1).replace('.', ',')} h</strong></span>
                  </div>
                );
              }
              return null;
            })()}

            {/* Adresse + lien maps (BT standard et SC) */}
            {!isInternal && bon.adresse && (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                data-testid="maps-link"
              >
                <Navigation className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm font-medium truncate">{bon.adresse}</span>
                <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </a>
            )}

            {/* Heure de début */}
            {bon.heure_debut && (
              <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400 text-sm">
                <Sun className="w-4 h-4" />
                <span>Début : <strong>{bon.heure_debut}</strong></span>
              </div>
            )}

            {/* Équipe */}
            {(bon.equipe?.length > 0 || bon.chef_equipe_nom) && (
              <div className="flex items-start gap-2 text-slate-600 dark:text-slate-400 text-sm">
                <Users className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="flex flex-wrap gap-1 min-w-0">
                  {bon.chef_equipe_nom && !bon.is_chef && (
                    <Badge className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300 text-xs">
                      👷 {bon.chef_equipe_nom}
                    </Badge>
                  )}
                  {(bon.equipe || []).slice(0, 4).map(m => (
                    <Badge key={m.id || `membre-${m.prenom}-${m.nom}`} className="bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300 text-xs">
                      {m.prenom} {m.nom?.charAt(0)}.
                    </Badge>
                  ))}
                  {(bon.equipe || []).length > 4 && (
                    <Badge className="bg-slate-100 text-slate-500 text-xs">+{bon.equipe.length - 4}</Badge>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Infos du punch en cours si pas de bon associé */}
        {!bon && isPunched && currentPunch?.adresse_chantier && (
          <div className="flex items-center gap-2 text-xs text-slate-600 bg-white/70 rounded-lg px-2.5 py-2">
            <Navigation className="w-3.5 h-3.5 shrink-0 text-slate-500" />
            <span className="truncate">{currentPunch.adresse_chantier}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          {isPaused ? (
            <Button
              onClick={() => navigate('/pointage')}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-semibold h-11"
              data-testid="manage-pause-btn"
            >
              <Coffee className="w-4 h-4 mr-2" />
              Gérer ma pause
            </Button>
          ) : !isPunched ? (
            isServiceCall ? (
              <Button
                onClick={() => navigate(bon?.dossier_id ? `/crm/projets/${bon.dossier_id}` : '/pointage')}
                className="flex-1 font-semibold h-11 text-white bg-orange-600 hover:bg-orange-700"
                data-testid="open-sc-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Ouvrir le Service Call
              </Button>
            ) : (
              <Button
                onClick={handlePunchIn}
                disabled={punching}
                className={`flex-1 font-semibold h-11 text-white ${
                  isInternal ? 'bg-purple-600 hover:bg-purple-700' : 'bg-orange-500 hover:bg-orange-600'
                }`}
                data-testid="punch-in-chantier-btn"
              >
                {punching
                  ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  : <Clock className="w-4 h-4 mr-2" />
                }
                Me puncher
              </Button>
            )
          ) : (
            <Button
              onClick={handlePunchOut}
              disabled={punching}
              variant="outline"
              className="flex-1 border-green-400 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 font-semibold h-11"
              data-testid="punch-out-chantier-btn"
            >
              {punching
                ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                : <CheckCircle2 className="w-4 h-4 mr-2" />
              }
              Fin de journée
            </Button>
          )}
          {bon && !isInternal && (
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-500 h-11 px-3"
              onClick={() => navigate(isServiceCall ? `/crm/projets/${bon.dossier_id}` : `/crm/projets/${bon.dossier_id}`)}
              data-testid="voir-bon-btn"
              title={isServiceCall ? 'Ouvrir le service call' : 'Ouvrir le dossier'}
            >
              <ArrowRight className="w-4 h-4" />
            </Button>
          )}
          {bon && isInternal && (
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-500 h-11 px-3"
              onClick={() => navigate('/pointage')}
              data-testid="voir-pointage-btn"
              title="Voir mes pointages"
            >
              <ArrowRight className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* 🆕 [Phase 51] Liste des chantiers suivants (2e, 3e...) — libellés
            "Job N" remplacés par ordinaux conformément à la refonte
            "Organisation des bons de travail" (suppression de Job1/Job2). */}
        {otherJobs.length > 0 && (
          <div className="border-t border-slate-200 dark:border-slate-700 pt-3 mt-1" data-testid="other-jobs-list">
            <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
              📋 Suite de la journée ({otherJobs.length} autre{otherJobs.length > 1 ? 's' : ''} {otherJobs.length > 1 ? 'tâches' : 'tâche'})
            </p>
            <ul className="space-y-1.5">
              {otherJobs.map((j, idx) => {
                const isSC = !!j._is_service_call;
                const jobNum = j.ordre_journee_index || (idx + 2);
                const mapsUrlJob = j.adresse ? `https://maps.google.com/?q=${encodeURIComponent(j.adresse)}` : null;
                return (
                  <li
                    key={j.id}
                    className={`flex items-start gap-2 rounded-lg p-2 text-xs border ${
                      isSC ? 'bg-orange-50 border-orange-200' : 'bg-white/70 border-slate-200 dark:bg-slate-800/40 dark:border-slate-700'
                    }`}
                    data-testid={`other-job-${j.id}`}
                  >
                    <Badge className={`flex-shrink-0 text-[10px] h-5 ${isSC ? 'bg-orange-500 text-white' : 'bg-slate-200 text-slate-700'}`}>
                      Job {jobNum}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 dark:text-slate-100 truncate">
                        {isSC ? '🔧 ' : ''}{j.client_nom || j.nom_dossier || j.description_travaux || 'Tâche'}
                      </p>
                      {j.adresse && (
                        <a
                          href={mapsUrlJob}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 truncate text-[11px]"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`other-job-maps-${j.id}`}
                        >
                          <Navigation className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{j.adresse}</span>
                        </a>
                      )}
                    </div>
                    {j.dossier_id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-slate-400 hover:text-blue-600 flex-shrink-0"
                        onClick={() => navigate(`/crm/projets/${j.dossier_id}`)}
                        data-testid={`other-job-open-${j.id}`}
                        title="Ouvrir"
                      >
                        <ArrowRight className="w-3 h-3" />
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* 🆕 [6.4-frontend] Section "Demain" — visible si BT aujourd'hui + BT demain.
            Boutons Punch in désactivés avec tooltip. */}
        {bonsTomorrow.length > 0 && (
          <div className="border-t-2 border-amber-200 dark:border-amber-700 pt-3 mt-1" data-testid="section-tomorrow">
            <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wide mb-2 flex items-center gap-1">
              <Sun className="w-3 h-3" /> Demain — {bonsTomorrow.length} bon{bonsTomorrow.length > 1 ? 's' : ''}
            </p>
            <ul className="space-y-1.5">
              {bonsTomorrow.map((j) => {
                const tomorrowDate = (j.dates_chantier || []).find(d => d > dateDuJourQuebec()) || '';
                const isSC = !!j._is_service_call;
                return (
                  <li
                    key={j.id}
                    className={`flex items-start gap-2 rounded-lg p-2 text-xs border ${isSC ? 'bg-orange-50 border-orange-200' : 'bg-amber-50 border-amber-200'}`}
                    data-testid={`tomorrow-job-${j.id}`}
                  >
                    <Badge className="flex-shrink-0 text-[10px] h-5 bg-amber-100 text-amber-800 border border-amber-300">
                      Demain
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 dark:text-slate-100 truncate">
                        {isSC ? '🔧 ' : ''}{j.client_nom || j.nom_dossier || j.description_travaux || 'Tâche'}
                      </p>
                      {j.adresse && (
                        <p className="text-[11px] text-slate-500 truncate">{j.adresse}</p>
                      )}
                    </div>
                    <Button
                      disabled
                      size="sm"
                      className="h-7 px-2 text-[10px] bg-slate-200 text-slate-500 cursor-not-allowed flex-shrink-0"
                      title={`Disponible à partir du ${tomorrowDate || 'jour du chantier'}`}
                      onClick={(e) => { e.stopPropagation(); toast.info("Tu pourras te punch in demain matin"); }}
                      data-testid={`tomorrow-punch-disabled-${j.id}`}
                    >
                      <LogIn className="w-3 h-3 mr-1" /> Demain
                    </Button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Lien secondaire vers page complète */}
        <button
          onClick={() => navigate('/pointage')}
          className="w-full text-center text-xs text-slate-400 hover:text-slate-600 -mt-1 py-1 transition-colors"
          data-testid="voir-tous-pointages-link"
        >
          Voir tous mes pointages <ChevronRight className="inline w-3 h-3" />
        </button>
      </CardContent>
    </Card>
  );
}
